"use client"

import { zodResolver } from "@hookform/resolvers/zod";
import { EspeceCreateSchema, EspeceFormData } from "@repo/types";
import { imageService } from "@repo/ui/lib/services/index";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateEspece, useEspece, useUpdateEspece, useUploadEspeceImage } from "../../../hooks/api/especes";

interface UseEspeceFormProps {
  id?: string;
  especeId?: number;
  onSuccess: () => void;
}

export function useEspeceForm({ id, especeId, onSuccess }: UseEspeceFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Convert id string to especeId number if provided
  const effectiveEspeceId = id ? parseInt(id, 10) : especeId;

  const { data: espece, isLoading } = useEspece(effectiveEspeceId || 0);
  const createEspece = useCreateEspece();
  const updateEspece = useUpdateEspece();
  const uploadImage = useUploadEspeceImage();
  const isEditing = !!effectiveEspeceId;

  const form = useForm<EspeceFormData>({
    resolver: zodResolver(EspeceCreateSchema as any),
    defaultValues: {
      name: '',
      nom_fr: '',
      cultivar: '',
      categorie: '',
      genre: '',
      tarif: 0,
    },
  });

  // Update form when data is loaded
  useEffect(() => {
    if (espece) {
      form.reset({
        name: espece.data.name || '',
        nom_fr: espece.data.nom_fr || '',
        cultivar: espece.data.cultivar || '',
        categorie: espece.data.categorie || '',
        genre: espece.data.genre || '',
        tarif: espece.data.tarif || 0,
      });

      const imgUrl = (espece.data as any)?.img_url;
      const primaryImage = imgUrl?.img1 ?? (typeof imgUrl === "string" ? imgUrl : null);
      if (primaryImage) {
        setPreviewUrl(imageService.getOptimizedImageUrl(primaryImage, "espece", 800, 600));
      } else {
        setPreviewUrl(null);
      }
    }
  }, [espece, form]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (data: EspeceFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Convert tarif to number if provided
      const formattedData = {
        ...data,
        tarif: parseFloat(data.tarif as any) || 0,
      };

      let createdId: number;
      if (isEditing && effectiveEspeceId) {
        await updateEspece.mutateAsync({
          id: effectiveEspeceId,
          data: formattedData as any
        });
        createdId = effectiveEspeceId;
        toast.success('Espèce mise à jour avec succès');
      } else {
        const result = await createEspece.mutateAsync(formattedData as any);
        createdId = (result as any).data.id as number;
        toast.success('Espèce créée avec succès');
      }

      if (imageFile && createdId) {
        await uploadImage.mutateAsync({ id: createdId, imageFile });
      }

      onSuccess();
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = isEditing
        ? 'Une erreur est survenue lors de la mise à jour'
        : 'Une erreur est survenue lors de la création';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    error,
    isEditing,
    isLoading,
    isSubmitting,
    previewUrl,
    handleImageChange,
    onSubmit,
  };
}
